/**
 * Inline SVG icons.
 *
 * Kept as components rather than image files so they inherit `currentColor` and
 * can be recoloured on hover. This also removes the project's only use of
 * `@ant-design/icons`, which wasn't even a declared dependency — it was being
 * pulled in transitively through antd and would have broken on a clean install.
 */

interface IconProps {
  size?: number;
  className?: string;
}

const base = (size: number) => ({
  width: size,
  height: size,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.5,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  "aria-hidden": true,
  focusable: false,
});

/** The site mark: a sheeted figure with hollow eyes. */
export const GhostMark = ({ size = 28, className }: IconProps) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 32 32"
    fill="none"
    className={className}
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="M16 2.5c-5.8 0-9.7 4.2-9.7 9.9v14.4c0 1.6 1.7 2.4 2.9 1.4l1.9-1.6a1.6 1.6 0 0 1 2.1 0l1.7 1.5a1.6 1.6 0 0 0 2.1 0l1.7-1.5a1.6 1.6 0 0 1 2.1 0l1.9 1.6c1.2 1 2.9.2 2.9-1.4V12.4c0-5.7-3.8-9.9-9.6-9.9Z"
      fill="currentColor"
      opacity="0.16"
    />
    <path
      d="M16 2.5c-5.8 0-9.7 4.2-9.7 9.9v14.4c0 1.6 1.7 2.4 2.9 1.4l1.9-1.6a1.6 1.6 0 0 1 2.1 0l1.7 1.5a1.6 1.6 0 0 0 2.1 0l1.7-1.5a1.6 1.6 0 0 1 2.1 0l1.9 1.6c1.2 1 2.9.2 2.9-1.4V12.4c0-5.7-3.8-9.9-9.6-9.9Z"
      stroke="currentColor"
      strokeWidth="1.6"
    />
    <ellipse cx="12.3" cy="13" rx="1.75" ry="2.3" fill="currentColor" />
    <ellipse cx="19.7" cy="13" rx="1.75" ry="2.3" fill="currentColor" />
  </svg>
);

export const IconMenu = ({ size = 24, className }: IconProps) => (
  <svg {...base(size)} className={className}>
    <path d="M3 6h18M3 12h18M3 18h18" />
  </svg>
);

export const IconClose = ({ size = 24, className }: IconProps) => (
  <svg {...base(size)} className={className}>
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
);

export const IconArrowUp = ({ size = 24, className }: IconProps) => (
  <svg {...base(size)} className={className}>
    <path d="M12 19V5M5 12l7-7 7 7" />
  </svg>
);

export const IconChevronLeft = ({ size = 24, className }: IconProps) => (
  <svg {...base(size)} className={className}>
    <path d="M15 5l-7 7 7 7" />
  </svg>
);

export const IconChevronRight = ({ size = 24, className }: IconProps) => (
  <svg {...base(size)} className={className}>
    <path d="M9 5l7 7-7 7" />
  </svg>
);

export const IconChevronDown = ({ size = 24, className }: IconProps) => (
  <svg {...base(size)} className={className}>
    <path d="M6 9l6 6 6-6" />
  </svg>
);

export const IconPlay = ({ size = 24, className }: IconProps) => (
  <svg {...base(size)} className={className}>
    <path d="M7 4.5v15l12-7.5-12-7.5Z" fill="currentColor" stroke="none" />
  </svg>
);

/**
 * The Discord mark, used only to link to the project's own Discord server —
 * the use Discord's brand guidelines are written for. Solid rather than
 * outlined so it reads at the large size the community panel uses.
 */
export const IconDiscord = ({ size = 24, className }: IconProps) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
    aria-hidden="true"
    focusable="false"
  >
    <path d="M20.317 4.3698a19.7913 19.7913 0 0 0-4.8851-1.5152.0741.0741 0 0 0-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 0 0-.0785-.037 19.7363 19.7363 0 0 0-4.8852 1.515.0699.0699 0 0 0-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 0 0 .0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 0 0 .0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 0 0-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 0 1-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 0 1 .0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 0 1 .0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 0 1-.0066.1276 12.2986 12.2986 0 0 1-1.873.8914.0766.0766 0 0 0-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 0 0 .0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 0 0 .0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 0 0-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189Z" />
  </svg>
);

export const IconLock = ({ size = 24, className }: IconProps) => (
  <svg {...base(size)} className={className}>
    <rect x="4" y="10.5" width="16" height="10.5" rx="2" />
    <path d="M8 10.5V7.5a4 4 0 0 1 8 0v3" />
  </svg>
);

export const IconCheck = ({ size = 24, className }: IconProps) => (
  <svg {...base(size)} className={className}>
    <path d="m4.5 12.5 5 5 10-11" />
  </svg>
);

export const IconAlert = ({ size = 24, className }: IconProps) => (
  <svg {...base(size)} className={className}>
    <path d="M12 3.5 1.8 20.5h20.4L12 3.5Z" />
    <path d="M12 10v4.5M12 17.6v.2" />
  </svg>
);

/* --- Feature icons ------------------------------------------------------ */

/** Two figures — co-op / multiplayer. */
export const IconSurvivors = ({ size = 24, className }: IconProps) => (
  <svg {...base(size)} className={className}>
    <circle cx="9" cy="7.5" r="3" />
    <circle cx="17" cy="9" r="2.25" />
    <path d="M3 20v-1.5A4.5 4.5 0 0 1 7.5 14h3A4.5 4.5 0 0 1 15 18.5V20" />
    <path d="M17 13.5h.5a3.5 3.5 0 0 1 3.5 3.5V20" />
  </svg>
);

/** An eye, half-lidded — the hunter. */
export const IconWatcher = ({ size = 24, className }: IconProps) => (
  <svg {...base(size)} className={className}>
    <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

/** A lantern — light as a resource. */
export const IconLantern = ({ size = 24, className }: IconProps) => (
  <svg {...base(size)} className={className}>
    <path d="M9 3h6M12 3v2.5" />
    <path d="M7.5 8.5h9l1 9.5a2 2 0 0 1-2 2.2h-7a2 2 0 0 1-2-2.2l1-9.5Z" />
    <path d="M7.5 8.5A4.5 4.5 0 0 1 12 5.5a4.5 4.5 0 0 1 4.5 3" />
    <path d="M12 11.5v5" />
  </svg>
);

/** A radio — voice chat / proximity comms. */
export const IconRadio = ({ size = 24, className }: IconProps) => (
  <svg {...base(size)} className={className}>
    <rect x="3" y="8.5" width="18" height="11.5" rx="2" />
    <circle cx="8" cy="14.5" r="2.75" />
    <path d="M14 12.5h4M14 16.5h4M17 8.5 8.5 4.5" />
  </svg>
);

/** A key — objectives / escape. */
export const IconKey = ({ size = 24, className }: IconProps) => (
  <svg {...base(size)} className={className}>
    <circle cx="8" cy="8" r="4.5" />
    <path d="M11.5 11.5 20 20M17 17l2-2M14.5 14.5l2-2" />
  </svg>
);

/** A map — locations. */
export const IconMap = ({ size = 24, className }: IconProps) => (
  <svg {...base(size)} className={className}>
    <path d="m3 6.5 6-2.5v13l-6 2.5v-13Z" />
    <path d="m9 4 6 2.5v13L9 17V4Z" />
    <path d="m15 6.5 6-2.5v13l-6 2.5v-13Z" />
  </svg>
);

/** Marks a link that leaves the site. */
export const IconExternal = ({ size = 24, className }: IconProps) => (
  <svg {...base(size)} className={className}>
    <path d="M14 4h6v6" />
    <path d="M20 4 11 13" />
    <path d="M18 14.5V19a1.5 1.5 0 0 1-1.5 1.5h-11A1.5 1.5 0 0 1 4 19V8a1.5 1.5 0 0 1 1.5-1.5H10" />
  </svg>
);

/**
 * Competitive mode: a rank badge, two chevrons on a shield.
 *
 * A trophy was the obvious choice and the wrong one — it reads as sport, and
 * at 18px the detail turns to mush. Chevrons on a shield say "league tier"
 * unambiguously, survive the small sizes, and stay an object like the rest of
 * this set rather than a floating abstract glyph.
 */
export const IconRank = ({ size = 24, className }: IconProps) => (
  <svg {...base(size)} className={className}>
    <path d="M12 2.5 20 6v6.2c0 4-3.2 7.4-8 9.3-4.8-1.9-8-5.3-8-9.3V6l8-3.5Z" />
    <path d="m8 13 4-3.2 4 3.2" />
    <path d="m8 16.8 4-3.2 4 3.2" />
  </svg>
);

export const ICONS: Record<string, (p: IconProps) => JSX.Element> = {
  survivors: IconSurvivors,
  watcher: IconWatcher,
  competitive: IconRank,
  lantern: IconLantern,
  radio: IconRadio,
  key: IconKey,
  map: IconMap,
};
